/**
 * Contrato del proveedor de reservas (CarryOn V3) — agnóstico del consumidor
 * (ADR-0004) y agnóstico de la implementación. El motor de reservas
 * (reservationEngine.ts) depende de esta interfaz, no de una implementación
 * concreta → se inyecta un cliente mock (tests/demo) o el live (HTTP GraphQL
 * en functions) sin tocar la lógica.
 *
 * Ver docs/architecture/CARRYON-API.md para el contrato real.
 */
import type { HotelRateKey } from './types'

/* ============== Tipos normalizados (aíslan el schema del proveedor) ============== */

/**
 * Cuántos, en cuántas habitaciones y cuántos por habitación.
 *
 * `paxPorHabitacion` es cuánta gente **va** en cada cuarto — no cuánta cabe, y
 * tampoco la categoría del cuarto. Los tres campos de habitación vienen en
 * `null` cuando el producto no tiene hospedaje (un paquete de puros tours):
 * significa "no digas nada de cuartos", no "cero cuartos".
 */
export interface Ocupacion {
  pax?: number | null
  habitaciones?: number | null
  paxPorHabitacion?: number | null
  /**
   * Cuántas personas CABEN en el cuarto, cuando el catálogo lo tiene capturado.
   *
   * Se lee y no se usa para nada más: no divide, no se enseña y jamás sustituye
   * a `paxPorHabitacion`. Existe porque durante un tiempo los dos datos vivían
   * en el mismo campo, y esa confusión costó dos cosas — un reparto imposible
   * en la tarjeta ("3 habitaciones de 4" para 6 personas) y, más caro, cuatro
   * personas durmiendo en un cuarto a tarifa de dos.
   *
   * Hoy llega `null` en todo el catálogo: nadie lo ha capturado.
   */
  capacidadHabitacion?: number | null
}

export interface SearchCriteria {
  placeId: string
  placeLabel: string
  arrival: string   // YYYY-MM-DD
  departure: string // YYYY-MM-DD
  adults: number
  childrenAges?: number[]
  /**
   * Dónde reside quien viaja. **Cambia el precio de los tours**: hay tarifa de
   * nacional y tarifa de extranjero (CVU v1.3.1 §3.1a).
   *
   * Omitirlo vale `nacional`, que es lo que veníamos mandando sin saberlo. Por
   * eso no se deduce del destino ni del origen del vuelo: un mexicano que vuela
   * desde Madrid sigue siendo nacional, y suponerlo al revés le cobraría de más.
   * O lo dice la persona, o va nacional.
   */
  residencia?: 'nacional' | 'extranjero'
}

export interface RoomOption {
  rateKey: HotelRateKey
  /** Código del hotel al que pertenece (insumo de checkrate/prebooking). */
  hotelCode: string
  code: string
  name: string
  price: number
  currency: string
  refundable: boolean
  mealPlan: string
  /** providerName de la habitación = nameBroker de los pasos siguientes. */
  nameBroker: string
  /**
   * A cuántos cubre ESTA tarifa.
   *
   * Vive en la habitación y no en el hotel porque es de la tarifa: un hotel
   * puede devolver una tarifa para dos y otra para cuatro, con precios
   * distintos, y cada tarjeta enseña la suya. Tomar la de la primera habitación
   * para todas fue un atajo que habría mentido en cuanto hubiera dos.
   */
  ocupacion?: Ocupacion
}

/**
 * Ficha del producto tal como la entrega el proveedor que la tiene. Los
 * mayoristas no la mandan (su contenido se resuelve por catálogo aparte); el
 * producto propio de UVAC sí, y es lo que el chat muestra.
 */
export interface ContenidoProducto {
  images?: string[]
  description?: string
  amenities?: string[]
}

/**
 * Qué clase de producto es. Una noche de hotel y un paquete de 4 días con dos
 * tours se cotizan igual —comparten `rateKey`— pero no se le presentan igual
 * al cliente.
 */
export interface ProductoInfo {
  tipo: 'hotel' | 'paquete' | 'tour'
  /** "4 días 3 noches", "8 horas". */
  duracion?: string
  /** Qué trae incluido, en líneas listas para mostrar. */
  incluye?: string[]
  /**
   * A cuántos cubre el precio y en cuántas habitaciones — **de la venta**.
   *
   * Sin esto un total de paquete es un número sin unidad, y quien lo lee acaba
   * dividiéndolo entre los viajeros para contestar "¿cuánto por persona?". Eso
   * inventa una tarifa que nadie publicó.
   *
   * `paxPorHabitacion` es cuánta gente va en cada habitación, NO la categoría
   * del cuarto: ver `carryon/ocupacion.ts`.
   */
  ocupacion?: Ocupacion
  /**
   * La ocupación **del producto**, la de la ficha publicada. No cambia con la
   * consulta: se pregunte por uno o por trece, es la misma.
   */
  ocupacionBase?: Ocupacion
  /** El precio de la ficha, para `ocupacionBase`. Tampoco cambia. */
  precioBase?: number
  /** Cuántas unidades del paquete hacen falta para el grupo. */
  unidades?: number
  /**
   * Lugares que se pagan y nadie ocupa (5 personas en un paquete de 2 → 1).
   *
   * Cobrarlo callado sería peor que no cobrarlo, y por eso el proveedor lo manda
   * como campo: para que se DIGA, no para que alguien lo deduzca.
   */
  lugaresSinUsar?: number
  /**
   * `packageCode` del paquete del que esta opción es una RECOMPOSICIÓN.
   *
   * Acomodar al grupo en menos habitaciones sale más barato y es una venta
   * legítima —una familia no se ofende por dormir de a tres—, pero no es el
   * paquete publicado. Viaja marcada y aparte, nunca en lugar de la ficha.
   */
  esAlternativaDe?: string
  /** Cuánto menos cuesta la recomposición que la ficha multiplicada. */
  ahorro?: number
  /** Cómo lo dice el proveedor, para que el sitio y el chat digan lo mismo. */
  comoSeDice?: string
}

export interface HotelOption {
  hotelCode: string
  name: string
  currency: string
  rooms: RoomOption[]
  /** Opcionales: solo los llenan los proveedores que entregan contenido. */
  content?: ContenidoProducto
  producto?: ProductoInfo
}

/** Banderas de cambio de tarifa (objeto `update` de CarryOn). */
export interface RateUpdate {
  price: boolean
  mealPlan: boolean
  cancellationPolicy: boolean
}

export interface RateResult {
  price: number
  currency: string
  refundable: boolean
  rateKey: HotelRateKey
  update: RateUpdate
}

export interface BookingResult {
  locator: string
  status: string
  /**
   * Referencia del SERVICIO dentro de la reserva. Travel Compositor identifica
   * cada servicio (el alojamiento) aparte del expediente, y la cancelación
   * necesita las dos. Los proveedores que no distinguen no la mandan.
   */
  serviceRef?: string
}

export interface Holder {
  firstName: string
  lastName: string
  /** Correo del titular: es a donde el operador manda el voucher. CVU lo exige. */
  email?: string
  /** WhatsApp del titular. CVU lo usa como identificador único; sin él la venta
   *  se cobra pero queda retenida en su Wallet. */
  phone?: string
}

export interface Occupant {
  name: string
  lastName: string
  type: 'adult' | 'child'
  age: number
}

/** Estado de la cotización releído del proveedor (oráculo de reconciliación). */
export interface QuoteState {
  status: string
  locator: string | null
  requiresRequote: boolean
}

/**
 * Cliente CarryOn. Las implementaciones (mock / live) cumplen este contrato.
 * Todas las llamadas son server-side en producción; el navegador nunca ve
 * el token ni la API key.
 */
export interface CarryonClient {
  search(criteria: SearchCriteria): Promise<{ hotelQuoteId: string; hotels: HotelOption[] }>
  checkrate(hotelQuoteId: string, room: RoomOption): Promise<RateResult>
  prebooking(
    hotelQuoteId: string,
    room: RoomOption,
    holder: Holder,
    occupants: Occupant[],
  ): Promise<RateResult>
  booking(hotelQuoteId: string, room: RoomOption): Promise<BookingResult>
  cancel(hotelQuoteId: string, locator: string, comment: string): Promise<{ cancelId: string; status: string }>
  quoteGet(hotelQuoteId: string): Promise<QuoteState>
}

/* ============================================================
 * Implementación MOCK (fixtures deterministas) — para tests y demo
 * ============================================================ */

export interface MockOptions {
  /** Simula que la tarifa cambió en el checkrate → dispara requote. */
  rateChangedOnCheckrate?: boolean
  /** Simula que el booking falla por red, pero el proveedor SÍ confirmó
   *  (la reconciliación por quoteGet debe recuperarlo). */
  bookingNetworkFailButConfirmed?: boolean
}

const MOCK_HOTELS: HotelOption[] = [
  {
    hotelCode: '680279',
    name: 'Riu Cancún',
    currency: 'MXN',
    rooms: [
      {
        rateKey: { rateKey: 'MTI1NTg5NjYzfDI=', code: ['A2C0-'], sessionId: 'sess-riu', rateDetailCode: '124930', promoCode: 'MAYORIST' },
        hotelCode: '680279', code: '124930', name: 'Doble Estándar', price: 3480, currency: 'MXN',
        refundable: true, mealPlan: 'AI', nameBroker: 'hotelbeds',
      },
    ],
  },
  {
    hotelCode: '551204',
    name: 'Iberostar Selección',
    currency: 'MXN',
    rooms: [
      {
        rateKey: { rateKey: 'QkVSU1RBUnwz', code: ['B3D1-'], sessionId: 'sess-ibe', rateDetailCode: '774521', promoCode: 'NRF' },
        hotelCode: '551204', code: '774521', name: 'Suite Junior Vista Mar', price: 5210, currency: 'MXN',
        refundable: false, mealPlan: 'AI', nameBroker: 'hotelbeds',
      },
    ],
  },
]

let mockCounter = 1000

/** Cliente CarryOn de mentiritas — determinista, sin red. */
export class MockCarryonClient implements CarryonClient {
  constructor(private opts: MockOptions = {}) {}

  async search(_criteria: SearchCriteria): Promise<{ hotelQuoteId: string; hotels: HotelOption[] }> {
    mockCounter += 1
    return { hotelQuoteId: `HQT-MOCK${mockCounter}`, hotels: MOCK_HOTELS }
  }

  async checkrate(_id: string, room: RoomOption): Promise<RateResult> {
    const changed = this.opts.rateChangedOnCheckrate === true
    return {
      price: changed ? room.price + 300 : room.price,
      currency: room.currency,
      refundable: room.refundable,
      rateKey: room.rateKey,
      update: { price: changed, mealPlan: false, cancellationPolicy: false },
    }
  }

  async prebooking(_id: string, room: RoomOption): Promise<RateResult> {
    return {
      price: room.price, currency: room.currency, refundable: room.refundable,
      rateKey: room.rateKey, update: { price: false, mealPlan: false, cancellationPolicy: false },
    }
  }

  async booking(_id: string, _room: RoomOption): Promise<BookingResult> {
    if (this.opts.bookingNetworkFailButConfirmed) {
      throw new Error('network timeout')
    }
    return { locator: `CO-MOCK-${mockCounter}`, status: 'CONFIRMED' }
  }

  async cancel(_id: string, _locator: string, _comment: string): Promise<{ cancelId: string; status: string }> {
    return { cancelId: `CX-${mockCounter}`, status: 'CANCELLED' }
  }

  async quoteGet(_id: string): Promise<QuoteState> {
    // Si el booking "falló por red" pero el proveedor confirmó, la
    // reconciliación lo descubre aquí.
    if (this.opts.bookingNetworkFailButConfirmed) {
      return { status: 'CONFIRMED', locator: `CO-MOCK-${mockCounter}`, requiresRequote: false }
    }
    return { status: 'OPEN', locator: null, requiresRequote: false }
  }
}
